import { Controller } from "@hotwired/stimulus"

/**
 * Single-layer parallax: background moves with pointer (desktop) and device tilt (mobile/iOS).
 * On iOS, device orientation is requested on first touch of the hero (required by the browser).
 *
 * Background image: viewport-based loading.
 * - Narrow (< 960px): mobile crop (left 45% of image) for better composition.
 * - Wide (>= 960px): 800px full, then lazily upgrade to 1200px when ready.
 */
const UPGRADE_VIEWPORT_MIN = 960

export default class extends Controller {
  static targets = ["bg"]
  static values = {
    imageUrl: String,
    imageUrlMobile: String,
    imageUrlLarge: String,
    videoUrl: String,
    videoPlaybackRate: { type: Number, default: 0.75 },
    maxShift: { type: Number, default: 18 },
    smoothing: { type: Number, default: 0.08 }
  }

  connect() {
    const viewportW = window.innerWidth
    const isNarrow = viewportW < UPGRADE_VIEWPORT_MIN
    const useVideo = this.hasVideoUrlValue && this.videoUrlValue

    if (this.hasBgTarget && !useVideo) {
      const url = isNarrow && this.hasImageUrlMobileValue
        ? this.imageUrlMobileValue
        : this.imageUrlValue
      if (url) {
        this.bgTarget.style.backgroundImage = `url("${url}")`
      }
    }

    if (!useVideo && !isNarrow && this.hasBgTarget && this.hasImageUrlLargeValue) {
      const img = new Image()
      img.onload = () => {
        if (this.hasBgTarget) {
          this.bgTarget.style.backgroundImage = `url("${this.imageUrlLargeValue}")`
        }
      }
      img.src = this.imageUrlLargeValue
    }

    // Explicit play() for hero background video – browsers often ignore autoplay attribute
    if (useVideo && this.hasBgTarget) {
      const video = this.bgTarget.querySelector(".hero-bg-video")
      if (video) {
        video.playbackRate = this.videoPlaybackRateValue
        const attemptPlay = () => {
          const p = video.play()
          if (p && typeof p.catch === "function") p.catch(() => {})
        }
        if (video.readyState >= 2) attemptPlay()
        else video.addEventListener("loadeddata", attemptPlay, { once: true })
      }
    }

    this.targetX = 0
    this.targetY = 0
    this.curX = 0
    this.curY = 0

    this.onPointerMove = this.onPointerMove.bind(this)
    this.animate = this.animate.bind(this)

    window.addEventListener("pointermove", this.onPointerMove, { passive: true })

    // iOS requires permission from a user gesture; request on first touch/click.
    // Use non-passive so the gesture is properly recognized; add click as fallback.
    if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
      this.boundRequestTilt = this.requestTiltOnTouch.bind(this)
      this.element.addEventListener("touchstart", this.boundRequestTilt, { once: true })
      this.element.addEventListener("click", this.boundRequestTilt, { once: true })
    } else if (typeof DeviceOrientationEvent !== "undefined") {
      this.addTiltListener()
    }

    this.raf = requestAnimationFrame(this.animate)
  }

  disconnect() {
    window.removeEventListener("pointermove", this.onPointerMove)
    if (this.boundRequestTilt) {
      this.element.removeEventListener("touchstart", this.boundRequestTilt)
      this.element.removeEventListener("click", this.boundRequestTilt)
    }
    if (this.onDeviceOrientation) {
      window.removeEventListener("deviceorientation", this.onDeviceOrientation)
    }
    cancelAnimationFrame(this.raf)
  }

  requestTiltOnTouch() {
    if (this.tiltPermissionRequested) return
    this.tiltPermissionRequested = true
    DeviceOrientationEvent.requestPermission()
      .then((res) => {
        if (res === "granted") this.addTiltListener()
      })
      .catch(() => {})
  }

  addTiltListener() {
    this.onDeviceOrientation = (e) => {
      // gamma: left-right tilt (-90..90). beta: front-back tilt; ~90 when upright in portrait.
      const gx = (e.gamma ?? 0) / 30
      const by = ((e.beta ?? 90) - 90) / 30 // normalize: 0 when upright
      this.targetX = Math.max(-1, Math.min(1, gx))
      this.targetY = Math.max(-1, Math.min(1, by))
    }
    window.addEventListener("deviceorientation", this.onDeviceOrientation, { passive: true })
  }

  onPointerMove(e) {
    const r = this.element.getBoundingClientRect()
    const x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
    const y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
    this.targetX = Math.max(-1, Math.min(1, x))
    this.targetY = Math.max(-1, Math.min(1, y))
  }

  animate() {
    this.curX += (this.targetX - this.curX) * this.smoothingValue
    this.curY += (this.targetY - this.curY) * this.smoothingValue

    const x = this.curX * this.maxShiftValue
    const y = this.curY * this.maxShiftValue

    if (this.hasBgTarget) {
      this.bgTarget.style.transform = `translate3d(${x}px, ${y}px, 0)`
    }
    this.raf = requestAnimationFrame(this.animate)
  }
}
